import { useEffect, useState } from 'react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { consentAskCopy } from '../studies/studyEligibility';
import LoadingSpinner from './LoadingSpinner';
import './StudyConsentDialog.css';

export default function StudyConsentDialog({
  handoff,
  onAccept,
  onDecline,
  error = '',
  busy = false,
  hasScheduledSurgery = true,
  onNeedSurgery = null,
}) {
  const studies = handoff?.studies || [];
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const copy = handoff ? consentAskCopy() : null;
  const showLoading = submitting || busy;

  useEffect(() => {
    setSelected([]);
    setSubmitting(false);
  }, [handoff]);

  const enroll = async () => {
    if (!hasScheduledSurgery) {
      onNeedSurgery?.();
      return;
    }
    setSubmitting(true);
    try {
      await onAccept?.(selected);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (studyId) => {
    setSelected((current) => (
      current.includes(studyId)
        ? current.filter((id) => id !== studyId)
        : [...current, studyId]
    ));
  };

  const chosen = studies.filter((study) => selected.includes(study.id));

  return (
    <Modal
      show={Boolean(handoff)}
      onHide={showLoading ? undefined : onDecline}
      centered
      animation={false}
      className="study-consent-modal"
      backdropClassName="study-consent-backdrop"
      aria-labelledby="study-consent-title"
    >
      <Modal.Header closeButton={!showLoading}>
        <Modal.Title id="study-consent-title">{copy?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showLoading ? (
          <LoadingSpinner text="Enrolling..." />
        ) : (
        <>
        <p className="study-consent-detail">{copy?.detail}</p>
        <Dropdown autoClose="outside" className="mt-3">
          <Dropdown.Toggle
            variant="outline-secondary"
            className="study-enrollment-toggle"
            id="study-enrollment-dropdown"
          >
            Select studies
          </Dropdown.Toggle>
          <Dropdown.Menu className="study-enrollment-menu">
            {studies.map((study) => (
              <Dropdown.Item
                key={study.id}
                as="button"
                type="button"
                active={selected.includes(study.id)}
                onClick={(event) => {
                  event.preventDefault();
                  toggle(study.id);
                }}
              >
                {study.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {chosen.length > 0 && (
          <div className="study-enrollment-pills">
            {chosen.map((study) => (
              <span key={study.id} className="study-enrollment-pill">
                {study.name}
                <button
                  type="button"
                  aria-label={`Remove ${study.name}`}
                  onClick={() => toggle(study.id)}
                  disabled={busy}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        {error && <p className="study-consent-error">{error}</p>}
        </>
        )}
      </Modal.Body>
      {!showLoading && (
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onDecline} disabled={busy}>
          Not now
        </Button>
        <Button
          variant="primary"
          disabled={busy || selected.length === 0}
          onClick={enroll}
        >
          Enroll
        </Button>
      </Modal.Footer>
      )}
    </Modal>
  );
}
